import { useEffect, useMemo, useState } from "react";

import { EDSList, EDSListItem } from "@/components/eds";
import EDSTabs from "@/components/eds/EDSTabs";
import { Switch } from "@/components/ui/switch";
import {
  EMAIL_DOMAINS,
  EVENT_CAPABILITIES
} from "../../../../../constants/permissions";
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { notification } from "../../../../../context/NotificationsContext";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { useFeatureFlagContext } from "../../../../../hooks/useFeatureFlagContext";
import usePermissions from "../../../../../hooks/usePermissions";
import { useUserContext } from "../../../../../hooks/useUserContext";
import { useUserPreferences } from "../../../../../hooks/useUserPreferences";
import { EvoFeedSubscriptionKey } from "../../../../../types/userPreferences";
import { getDevice } from "../../../../../utils/device";
import { dmnIdentity } from "../../../../../utils/dmnIdentity";
import { dmnStorage } from "../../../../../utils/dmnStorage";
import {
  getBuildNumber,
  getEnvironmentProps,
  getVersionNumber,
  isBrowser
} from "../../../../../utils/environment";
import { parseToken } from "../../../../../utils/helpers";
import { WebViewTooltip } from "../../../../Pages/Diary/SubHeader/WebViewTooltip";
import { Button } from "../../../../Shared/Buttons/Button";
import { SubTitle } from "../../../../Shared/Headings/Subtitle/SubTitle";
import { GroundhogDayModal } from "../../GroundhogDayModal";

const EVO_FEED_SUBSCRIPTION_LABELS: Record<EvoFeedSubscriptionKey, string> = {
  new_autoconfirmed_bookings: "New auto-confirmed bookings",
  new_same_day_autoconfirmed_bookings: "New same-day auto-confirmed bookings",
  new_enquiries: "New enquiries",
  user_mentions: "User mentions",
  payment_disputes: "Payment disputes"
};

const EVO_FEED_SUBSCRIPTION_DESCRIPTIONS: Record<
  EvoFeedSubscriptionKey,
  string
> = {
  new_autoconfirmed_bookings:
    "Receive notifications when bookings are auto-confirmed.",
  new_same_day_autoconfirmed_bookings:
    "Receive notifications for same-day auto-confirmed bookings only.",
  new_enquiries: "Receive notifications when new enquiries arrive.",
  user_mentions: "Receive notifications when you are mentioned.",
  payment_disputes: "Receive notifications for payment disputes."
};

export const SettingsContent = () => {
  const featureFlagContext = useFeatureFlagContext();
  const userContext = useUserContext();
  const { colourScheme, setTheme } = useDeviceContext();
  const { venueGroupConfig } = useAppContext();
  const { roles, capabilities } = usePermissions();
  const {
    preferences,
    isLoading: isLoadingPreferences,
    isSaving,
    hasLoadError,
    loadPreferences,
    updateAndSaveSubscription
  } = useUserPreferences();
  const canViewDebug = userContext.user?.email?.includes(
    EMAIL_DOMAINS.THE_ACCESS_GROUP
  );
  const canViewFeatureFlags = userContext.user?.capabilities?.includes(
    EVENT_CAPABILITIES.HAS_EARLY_ACCESS_PREVIEW
  );
  // Match collins-ui logic: show Evo Feed section if venue group uses evo feeds or is on evo tier
  const showEvoFeedSection = !!(
    venueGroupConfig &&
    (venueGroupConfig.collins_features?.use_evo_feeds ||
      venueGroupConfig.product_tier === "evo")
  );

  // Match collins-ui logic: user can manage refunds AND payment disputes feature is enabled
  const canManageRefunds =
    roles.booking?.refunds?.create ||
    capabilities.manageVenueGroupRefunds?.some ||
    capabilities.manageVenueRefunds?.some;
  const enablePaymentDisputesOnFeed =
    venueGroupConfig?.collins_features?.enable_payment_disputes_on_feed ===
    true;
  const canViewPaymentDisputesPreference =
    canManageRefunds && enablePaymentDisputesOnFeed;
  const [aToken, setAToken] = useState("");
  const [dToken, setDToken] = useState("");
  const showTheme = useMemo(isBrowser, []);

  const [storageItems, setStorageItems] = useState<any>({});
  const [otherItems, setOtherItems] = useState<any>({});
  const [isGroundhogDayModalOpen, setIsGroundhogDayModalOpen] = useState(false);

  const featureFlags = featureFlagContext.featureFlags.filter(
    (flag) => !(flag.key === "alphaHeader" && getDevice() === "mobile") // Exclude alphaHeader from settings as it's live for mobile
  );

  const selectColourScheme = (themeName: "Light" | "Dark" | "System") => {
    dmnStorage.add("theme", themeName);
    setTheme(themeName);
  };

  useEffect(() => {
    const getTokens = async () => {
      const aToken = (await dmnIdentity.getAccessToken()) ?? "";
      const dToken = (await dmnIdentity.getDMNAccessToken()) ?? "";

      setAToken(aToken ? `${aToken.substring(0, 50)}...` : "Not found");
      setDToken(dToken ? `${dToken.substring(0, 50)}...` : "Not found");
    };

    const getStorageItems = async () => {
      setStorageItems({
        venueId: await dmnStorage.get("venueId"),
        SubscriptionId: await dmnStorage.get("SubscriptionId"),
        user: (await dmnStorage.get("user"))?._id || "No user id",
        gridConfig: JSON.stringify(await dmnStorage.get("gridConfig")),
        featureFlags: JSON.stringify(await dmnStorage.get("featureFlags"))
      });

      const token = await dmnIdentity.getAccessToken();
      const tokenParts = parseToken(token);

      setOtherItems({
        token: JSON.stringify(tokenParts),
        envVars: JSON.stringify(getEnvironmentProps(token))
      });
    };

    getTokens();
    getStorageItems();
  }, []);

  useEffect(() => {
    if (showEvoFeedSection) {
      loadPreferences();
    }
  }, [showEvoFeedSection, loadPreferences]);

  const handleToggleSubscription = async (
    key: EvoFeedSubscriptionKey,
    checked: boolean
  ) => {
    const success = await updateAndSaveSubscription(key, checked);
    if (success) {
      notification({
        heading: "Preferences saved successfully.",
        toastType: "success"
      });
    } else {
      notification({
        heading: "Failed to save preferences. Please try again.",
        toastType: "error"
      });
    }
  };

  const isDesktop = getDevice() === "desktop";
  return (
    <div
      className={[
        "SettingsContent",
        "h-[85vh] mb-[1%] justify-center",
        !isDesktop
          ? "withoutNavigationBar w-full flex overflow-auto absolute grow"
          : "WithNavigationBar flex overflow-auto relative grow"
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="SettingsContent__body w-[70%] m-[calc(var(--panelPadding)*2)] h-full flex-col max-[480px]:w-full">
        {showTheme ? (
          <>
            <SubTitle>Display Options</SubTitle>
            <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
              <EDSListItem>
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1">
                    <strong>Theme</strong>
                    <br />
                    <small>
                      Allows you to determine what the theme of the app is.
                    </small>
                  </div>
                  <div>
                    <div className="InlineSettingsTabsWrapper">
                      <EDSTabs
                        tabs={[
                          { id: "light", label: "Light" },
                          { id: "dark", label: "Dark" }
                        ]}
                        selectedTab={colourScheme.toLowerCase()}
                        onTabChange={(tabId) => {
                          selectColourScheme(
                            tabId === "light" ? "Light" : "Dark"
                          );
                        }}
                        className="InlineTabs"
                      />
                    </div>
                  </div>
                </div>
              </EDSListItem>
            </EDSList>
          </>
        ) : null}
        {showEvoFeedSection && (
          <>
            <SubTitle>Evo Feed Subscriptions</SubTitle>
            {isLoadingPreferences ? (
              <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
                <EDSListItem>
                  <span>Loading preferences...</span>
                </EDSListItem>
              </EDSList>
            ) : (
              <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
                {(
                  Object.keys(
                    EVO_FEED_SUBSCRIPTION_LABELS
                  ) as EvoFeedSubscriptionKey[]
                ).map((key) => {
                  if (
                    key === "payment_disputes" &&
                    !canViewPaymentDisputesPreference
                  ) {
                    return null;
                  }

                  const isDisabledBySameDayRule =
                    key === "new_same_day_autoconfirmed_bookings" &&
                    preferences.evo_feed_subscriptions
                      .new_autoconfirmed_bookings;

                  return (
                    <EDSListItem key={key}>
                      <div className="flex items-center justify-between w-full">
                        <div className="flex-1">
                          <strong>
                            {EVO_FEED_SUBSCRIPTION_LABELS[key]}
                            {isDisabledBySameDayRule && (
                              <WebViewTooltip
                                content="This is automatically enabled when \u201CNew auto-confirmed bookings\u201D is on."
                                side="top"
                                sideOffset={4}
                              >
                                <span
                                  className="inline-flex items-center justify-center ml-1 w-4 h-4 rounded-full border border-current text-xs cursor-help align-middle"
                                  aria-label="Why is this toggle disabled?"
                                >
                                  ?
                                </span>
                              </WebViewTooltip>
                            )}
                          </strong>
                          {isDisabledBySameDayRule && (
                            <span
                              id={`${key}-disabled-reason`}
                              className="sr-only"
                            >
                              This is automatically enabled when &ldquo;New
                              auto-confirmed bookings&rdquo; is on.
                            </span>
                          )}
                          <br />
                          <small>
                            {EVO_FEED_SUBSCRIPTION_DESCRIPTIONS[key]}
                          </small>
                        </div>
                        <div>
                          <Switch
                            checked={preferences.evo_feed_subscriptions[key]}
                            disabled={
                              isDisabledBySameDayRule ||
                              isSaving ||
                              hasLoadError
                            }
                            aria-label={`Toggle ${EVO_FEED_SUBSCRIPTION_LABELS[key]} notifications`}
                            aria-describedby={
                              isDisabledBySameDayRule
                                ? `${key}-disabled-reason`
                                : undefined
                            }
                            onCheckedChange={(checked) => {
                              handleToggleSubscription(key, checked);
                            }}
                          />
                        </div>
                      </div>
                    </EDSListItem>
                  );
                })}
              </EDSList>
            )}
          </>
        )}
        <SubTitle>App Infomation</SubTitle>
        <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
          <EDSListItem>
            <div className="flex items-center justify-between w-full">
              <div className="flex-1">
                <strong>Version</strong>
              </div>
              <div>{`${getVersionNumber()}.${getBuildNumber()}`}</div>
            </div>
          </EDSListItem>
        </EDSList>
        {venueGroupConfig?.product_tier === "evo" && (
          <>
            <SubTitle>AI Features</SubTitle>
            <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
              <EDSListItem>
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1">
                    <strong>AI Co-Pilot</strong>
                    <br />
                    <small>
                      Enable AI-powered insights and explanations in Space
                      Optimiser. Your data remains private to your venue.
                    </small>
                  </div>
                  <div>
                    <Switch
                      checked={featureFlagContext.isFlagEnabled(
                        "showAICoPilot"
                      )}
                      onCheckedChange={(checked) => {
                        featureFlagContext.toggleFlag("showAICoPilot", checked);
                      }}
                    />
                  </div>
                </div>
              </EDSListItem>
            </EDSList>
          </>
        )}
        {canViewFeatureFlags && (
          <>
            <SubTitle>Feature flags</SubTitle>
            <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
              {featureFlags.map((featureFlag) => (
                <EDSListItem key={featureFlag.key}>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1">
                      <strong>{featureFlag.title}</strong>
                      <br />
                      <small>{featureFlag.description}</small>
                    </div>
                    <div>
                      <Switch
                        checked={featureFlag.isEnabled}
                        onCheckedChange={(checked) => {
                          featureFlagContext.toggleFlag(
                            featureFlag.key,
                            checked
                          );
                        }}
                      />
                    </div>
                  </div>
                </EDSListItem>
              ))}
            </EDSList>
          </>
        )}
        {canViewDebug && (
          <>
            <SubTitle>Token Values</SubTitle>
            <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
              <EDSListItem>
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1">
                    <strong>Access Token</strong>
                  </div>
                  <div>
                    <div>{aToken ?? "Not set"}</div>
                  </div>
                </div>
              </EDSListItem>
              <EDSListItem>
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1">
                    <strong>DMN Token</strong>
                  </div>
                  <div>
                    <div>{dToken ?? "Not set"}</div>
                  </div>
                </div>
              </EDSListItem>
            </EDSList>
            <SubTitle>Test Data Generation</SubTitle>
            <Button onClick={() => setIsGroundhogDayModalOpen(true)}>
              Groundhog Day Generator
            </Button>
            <GroundhogDayModal
              isOpen={isGroundhogDayModalOpen}
              onClose={() => setIsGroundhogDayModalOpen(false)}
            />

            <SubTitle>Storage Tests (dmnStorage)</SubTitle>
            <Button
              onClick={async () => {
                alert("Adding Ping");
                dmnStorage.add("Ping", {
                  Response: "Pong - DMN Store"
                });
              }}
            >
              Add storage via dmn storage
            </Button>
            <Button
              onClick={async () => {
                alert("Getting Ping");
                const pong = await dmnStorage.get("Ping");
                alert(JSON.stringify(pong));
              }}
            >
              Read storage via dmn storage
            </Button>
            <Button
              onClick={async () => {
                alert("Deleting Ping");
                dmnStorage.delete("Ping");
              }}
            >
              Delete storage via dmn storage
            </Button>
            <SubTitle>Storage Debug</SubTitle>
            <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
              <EDSListItem>
                venueId {JSON.stringify(storageItems.venueId)}
              </EDSListItem>
              <EDSListItem>
                SubscriptionId {JSON.stringify(storageItems.SubscriptionId)}
              </EDSListItem>
              <EDSListItem>DMN User {storageItems.user}</EDSListItem>
              <EDSListItem>ridConfig {storageItems.gridConfig}</EDSListItem>
              <EDSListItem>
                featureFlags {storageItems.featureFlags}
              </EDSListItem>
            </EDSList>
            <EDSList className="SettingsContentList w-full [overflow-wrap:anywhere]">
              <h2>Environment</h2>
              <SubTitle>Environment</SubTitle>

              <EDSListItem>token {otherItems.token}</EDSListItem>
              <EDSListItem>envVars {otherItems.envVars}</EDSListItem>
            </EDSList>
          </>
        )}
      </div>
    </div>
  );
};
